import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MessageModel } from "../../../../models/message.model";
import { AuthService } from "../../../../services/auth.service";
import { MessageService } from "../../../../services/message.service";
import { UtilityService } from "../../../../services/utility.service";
import { MessageFormProperty } from "../../../../types/message-types";
import { ActivatedRoute } from "@angular/router";

@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MessageFormComponent implements OnInit {
  protected _message: MessageModel = new MessageModel();
  @Input() set message(val: MessageModel) {
    this.messageService.showErrors = false;
    this.messageService.errors.clear();

    //Check for new window or existing message to set up the form with the correct values.
    let routeId = this.activatedRoute.snapshot.paramMap.get("id");
    if (val.id || routeId) {
      this.setUpExistingMessage(val);
      this.messageService.originalMessage = UtilityService.createMessageCopy(this.messageService.currentMessage!);
    } else {
      this.setUpDefaultValues(val);
      this.messageService.originalMessage = UtilityService.createMessageCopy(this.messageService.currentMessage!);
    }
  }
  get message(): MessageModel {
    return this._message!;
  }

  private _numberOfVersions = 1;
  @Input() set numberOfVersions(val: number) {
    this._numberOfVersions = val;
    this.applyReadOnlyState();
  }
  get numberOfVersions(): number {
    return this._numberOfVersions;
  }

  messageForm: FormGroup = null!;

  constructor(
    protected authService: AuthService,
    protected messageService: MessageService,
    protected activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {}
  setUpDefaultValues(val: MessageModel) {}
  setUpExistingMessage(val: MessageModel) {}

  protected applyReadOnlyState() {
    if (!this.messageForm) return;

    const shouldBeReadOnly = this.isReadOnly();

    Object.keys(this.messageForm.controls).forEach(key => {
      const control = this.messageForm.get(key);
      if (control) {
        if (shouldBeReadOnly) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      }
    });
  }

  getCount(property: MessageFormProperty) {
    const formValue = this.messageForm?.get(property)?.value;
    if (formValue != null && formValue !== '') {
      return String(formValue).length;
    }
    if (this.message[property]) {
      return this.message[property].length;
    } else if (this.message.info[property]) {
      return this.message.info[property].length;
    } else {
      return 0;
    }
  }

  showErrors(property) {
    return (
      this.messageService.showErrors && this.messageService.getError(property)
    );
  }

  onDateTextInvalid(invalid: boolean, property: MessageFormProperty) {
    if (invalid) {
      const error = { invalidDate: true };
      this.messageService.setError(property, error);
      this.messageForm.get(property)?.setErrors(error);
      return;
    }
    const current = this.messageService.getError(property);
    if (current?.invalidDate) {
      this.messageService.setError(property, null);
      this.messageForm.get(property)?.setErrors(null);
    }
  }

  onDateChange(event, property: MessageFormProperty) {
    this.messageForm.get(property)!.patchValue(event);
    this.message[property] = event;

    let err =
      !event || UtilityService.validDate(event) ? null : { invalidDate: true };
    this.messageService.setError(property, err);
    this.messageForm.get(property)!.setErrors(err);

    let dateOrderErr = UtilityService.compareDates(
      this.message.validFrom!,
      this.message.validTo!
    )
      ? null
      : { wrongDateOrder: true };
    this.messageService.setError(property, dateOrderErr);
    this.messageForm.get(property)!.setErrors(dateOrderErr);

    if (this.message[property] && this.message[property].setSeconds) this.message[property].setSeconds(0);
  }

  onTextboxPaste(event: ClipboardEvent, property: string, maxLimit: number, required: boolean) {
    // Timeout needed to get pasted text AND text from input.
    // Without timeout only old value will be available.
    setTimeout(() => this.onTextboxChange(event, property, maxLimit, required), 0);
  }

  onTextboxChange(event, property: string, maxLimit: number, required: boolean) {
    const target = event.target as HTMLTextAreaElement;
    const text = target.value ?? target.innerText ?? '';
    let error: any = null;
    if (required && (!text || text.length == 0)) {
      error = { required: true };
    } else if (maxLimit && text.length > maxLimit) {
      error = { maxLimit: true };
    }
    this.messageForm.get(property)!.setErrors(error);
    this.messageService.setError(property, error);
    if (this.message.hasOwnProperty(property)) {
      this.message[property] = text;
    } else if (this.message.info.hasOwnProperty(property)) {
      this.message.info[property] = text;
    }
  }

  setInitialErrors(property: MessageFormProperty) {
    if (!this.message[property]) {
      let error = { required: true };
      this.messageForm.get(property)!.setErrors(error);
      this.messageForm.get(property)!.markAsTouched();
      this.messageService.setError(property, error);
    }
  }

  errorMessage(property: MessageFormProperty) {
    let message: any = [];
    let formCtrl = this.messageForm.get(property);
    if (this.messageService.errors.get(property)["required"]) {
      message.push("Måste fyllas i");
    }

    if (formCtrl!.hasError("maxLimit")) {
      message.push("Text är för lång");
    }

    if (formCtrl!.hasError("wrongDateOrder")) {
      message.push("Visas till är mindre än visas från");
    }
    return message.join("/");
  }

  isReadOnly() {
    return (
      this.message.ver < this.numberOfVersions || !this.authService.hasReadWriteGroups()
    );
  }

  protected prependKlart(value: string | null | undefined): string {
    const current = value ?? '';
    if (current.trimStart().toUpperCase().startsWith('KLART:')) {
      return current;
    }
    return current ? `KLART: ${current}` : 'KLART:';
  }

  protected setTextField(
    property: MessageFormProperty,
    value: string,
    options?: { required?: boolean; maxLimit?: number; domId?: string }
  ) {
    const control = this.messageForm?.get(property);
    if (control) {
      control.setValue(value);
    }

    if (Object.prototype.hasOwnProperty.call(this.message, property)) {
      this.message[property] = value;
    } else if (this.message.info && Object.prototype.hasOwnProperty.call(this.message.info, property)) {
      this.message.info[property] = value;
    }

    let error: { required?: boolean; maxLimit?: boolean } | null = null;
    if (options?.required && !value) {
      error = { required: true };
    } else if (options?.maxLimit && value.length > options.maxLimit) {
      error = { maxLimit: true };
    }
    this.messageService.setError(property, error);
    control?.setErrors(error);

    const domId = options?.domId ?? `${property}Div`;
    const element = document.getElementById(domId) as HTMLTextAreaElement | null;
    if (element) {
      element.value = value;
    }
  }

  limitUserInput(event, maxLimit: number) {
    let allowRowbreak = maxLimit >= 100;
    if (!allowRowbreak && event.key == "Enter") {
      event.preventDefault();
    } else if (event.key != "Backspace") {
      const length = (event.target as HTMLTextAreaElement).value?.length
        ?? event.target.innerText?.length
        ?? 0;
      if (length + 1 > maxLimit) {
        if (event.ctrlKey && (event.key == "KeyX" || event.key == "KeyC")) {
          //DO nothing?
        } else {
          event.preventDefault();
        }
      }
    }
  }

}
